import { useContext, useEffect, useMemo, useState } from "react";
import { Controller, SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { UserContext } from "../../hooks/UserContext";
import { getJobPriorities } from "../../utilities/types";
import { GeoData } from "@rogoag/shared-ts";
import {
	TextField,
	Checkbox,
	Select,
	MenuItem,
	Button,
	Box,
	Typography,
	Grid,
	Accordion,
	AccordionDetails,
	Autocomplete,
	Tooltip,
	createFilterOptions,
} from "@mui/material";
import AccordianSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormField from "../helpers/FormField";
import { CREATE_NEW_GROWER, CREATE_NEW_SALESPERSON } from "../../utilities/constants";
import { toast } from "react-toastify";
import PortalTooltip from "../helpers/PortalTooltip";
import { LoadingComponent } from "../helpers/LoadingComponent";
import { API, Branch, Company, Contact, Deal, defaultJobForImport, Grower, Job } from "../../api";
import { Oops } from "../../utilities/oops";
import { Publish, Upload } from "@mui/icons-material";
import { getDealLabel } from "../../utilities/utils";
import {
	getFormValidationRules,
	isFieldRequired,
	isFieldVisible,
	ValidationContext,
	FileType,
} from "../../utilities/jobValidation";
import { ImportTableColumn } from "../../utilities/types";
import { AuthUser } from "../../api/common";
import { columnDisplayNames } from "../admin/ImportSettings";

interface ManualEntryFormProps extends Partial<Job> {
	isZone: boolean;
	growers: Grower[];
	selectedDeal: Deal;
	allDeals: Deal[];
	company: Company | null;
	selectedBranches: Branch[];
	branches?: Branch[];
	lastCropOptions?: string[];
	nextCropOptions?: string[];
	salespersons?: Contact[];
}

const filter = createFilterOptions<Grower | { id: string; name: string }>();

/**
 * Get default form values for job creation
 * Single source of truth for both initial form state and reset values
 */
function getDefaultFormValues(props: ManualEntryFormProps, userContext: AuthUser | undefined): Partial<Job> {
	return {
		...defaultJobForImport(),
		growerIds: [CREATE_NEW_GROWER],
		submitterEmail: userContext?.email,
		submitterName: userContext?.full_name,
		branchIds: props.selectedBranches?.length > 0 ? [props.selectedBranches[0].id] : [],
		dealIds: props.selectedDeal ? [props.selectedDeal.id] : ["custom"],
		frequencyOfMicros: props.selectedDeal?.frequencyOfMicros,
		density: props.selectedDeal?.density,
		depth: props.selectedDeal?.depth,
		testPackage: props.selectedDeal?.testPackage,
	};
}

export function ManualEntryForm(props: ManualEntryFormProps) {
	const userContext = useContext(UserContext);
	const [growerSelected, setGrowerSelected] = useState<Grower | { id: string; name: string } | null>(null);
	const [salespersonSelected, setSalespersonSelected] = useState<Contact | { id: string; name: string } | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [deal, setDeal] = useState<Deal | { id: "custom"; label: "Create New Configuration" }>(props.selectedDeal);
	const [microsRaw, setMicrosRaw] = useState("");
	const [selectedBranch, setSelectedBranch] = useState<Branch | null>(
		props.selectedBranches?.length > 0 ? props.selectedBranches[0] : null,
	);

	const {
		register,
		handleSubmit,
		watch,
		control,
		reset,
		setValue,
		trigger,
		formState: { errors, isValid },
	} = useForm<Job>({
		mode: "onBlur", // Validate when field loses focus (better performance than onChange)
		reValidateMode: "onBlur", // Re-validate on blur after first submission attempt
		defaultValues: getDefaultFormValues(props, userContext),
	});

	// Watch form fields needed for validation context
	const pointsFiles = watch("files.pointsSHP");
	const salespersonIds = watch("salespersonIds");

	// Validation context for this component - reactive to points file changes and salesperson selection
	const validationContext = useMemo(() => {
		return {
			company: props.company ?? undefined,
			deal: deal.id === "custom" ? undefined : (deal as Deal),
			user: userContext ?? undefined,
			hasPointsFile: !!(pointsFiles && pointsFiles.length > 0),
			isCreatingNewSalesperson: salespersonIds?.at(0) === CREATE_NEW_SALESPERSON,
		};
	}, [props.company, deal, userContext, pointsFiles, salespersonIds]);

	// Watch deal-related fields for auto-updating deal configuration
	const frequencyOfMicros = watch("frequencyOfMicros");
	const density = watch("density");
	const depth = watch("depth");
	const testPackage = watch("testPackage");
	const dealIds = watch("dealIds");

	useEffect(() => {
		updateDeal();
	}, [frequencyOfMicros, density, depth, testPackage, dealIds, deal]);

	// Clear microsList when a pointsSHP list is emptied
	useEffect(() => {
		const subscription = watch((value, { name }) => {
			const pointsFiles = value.files?.pointsSHP;
			if (name === "files.pointsSHP" && (!pointsFiles || pointsFiles.length === 0)) {
				setMicrosRaw("");
			}
		});

		return () => subscription.unsubscribe();
	}, [watch, setValue]);

	const updateDeal = () => {
		const deal = props.allDeals.find((deal: Deal) => {
			return deal.id === dealIds?.at(0);
		});
		if (deal) {
			setDeal(deal);
			setValue("dealIds", [deal.id]);
		} else {
			setDeal({ id: "custom", label: "Create New Configuration" });
			setValue("dealIds", ["custom"]);
		}
	};

	const getDealLabelForDropdown = (option: Deal | { id: string; label: string }) => {
		if (option && option.id === "custom") {
			return "Create New Configuration";
		}

		return getDealLabel(option as Deal);
	};

	const onSubmit: SubmitHandler<Job> = async (data) => {
		setLoading(true);
		try {
			// Validate the form before submitting
			const isFormValid = await trigger();
			if (!isFormValid) {
				toast.error("Please fill in all required fields correctly.");
				setLoading(false);
				return;
			}

			if (data.dealIds?.at(0) === "custom") {
				if (!window.confirm("Custom Configuration Selected. Proceed?")) {
					setLoading(false);
					return;
				}
			}
			if (data.dealIds?.at(0) === "custom") {
				data.dealIds = [];
			}
			if (data.growerIds?.at(0) === CREATE_NEW_GROWER) {
				data.growerIds = [];
			}
			if (data.importGrowerName && data.importGrowerName.startsWith("New Grower: ")) {
				data.importGrowerName = data.importGrowerName.replace(/^New Grower: /, "");
			}
			if (selectedBranch) {
				data.branchIds = [selectedBranch.id];
			}

			if (data.growerIds.length > 0 && data.growerIds[0] !== "" && data.growerPhoneNumber !== "") {
				await API.Data.Growers.update.phoneNumber(data.growerIds[0], data.growerPhoneNumber ?? "");
			}
			await toast.loading("Job submitting...");

			const newJob = defaultJobForImport(
				data.fieldName,
				data.depth,
				data.density,
				data.importGrowerName,
				data.frequencyOfMicros,
				data.testPackage,
				data.farmName,
				data.labName,
			);

			newJob.growerIds = data.growerIds || [];
			newJob.isTestJob = data.isTestJob || false;
			newJob.microsList = data.microsList;
			newJob.collectBoundary = data.collectBoundary;
			newJob.isPointsAttached = data.isPointsAttached || false;
			newJob.eventId = data.eventId || "";
			newJob.submitterEmail = data.submitterEmail;
			newJob.submitterName = data.submitterName;
			newJob.submissionNotes = data.submissionNotes || "";
			newJob.growerPhoneNumber = data.growerPhoneNumber;
			newJob.labInstructions = data.labInstructions || "";
			newJob.isReadyToSample = data.isReadyToSample || false;
			newJob.dealIds = data.dealIds || [];
			newJob.branchIds = data.branchIds || [];
			newJob.files = data.files || {};
			newJob.fieldId = data.fieldId || "";
			newJob.labAccountNumber = data.labAccountNumber;
			newJob.labSubmissionCode = data.labSubmissionCode;
			newJob.lastCrop = data.lastCrop;
			newJob.nextCrop = data.nextCrop;
			newJob.yieldGoal = data.yieldGoal || "";
			newJob.dateFieldReadyEstimated = data.dateFieldReadyEstimated;
			newJob.salespersonIds = data.salespersonIds || [];
			newJob.newSalesperson = data.newSalesperson;
			newJob.externalLabId = data.externalLabId || "";
			newJob.externalId = data.externalId || "";
			newJob.externalFarmId = data.externalFarmId || "";

			// If CREATE_NEW_SALESPERSON, clear the sentinel value
			if (newJob.salespersonIds.includes(CREATE_NEW_SALESPERSON)) {
				newJob.salespersonIds = [];
			}

			await API.Data.Jobs.create.one(newJob, "Created Portal Single");
			await toast.dismiss();
			setLoading(false);
			await toast.success("Job submitted successfully!");

			// Reset form to default values
			reset(getDefaultFormValues(props, userContext));
			setMicrosRaw("");
			setGrowerSelected(null);
			setSalespersonSelected(null);
			setSelectedBranch(props.selectedBranches?.length > 0 ? props.selectedBranches[0] : null);
			setDeal(props.selectedDeal);
		} catch (error: any) {
			await toast.dismiss();
			Oops.handleError(error, "SubmitJobs.ManualEntryForm.onSubmit");
		} finally {
			setLoading(false);
		}
	};

	const onError: SubmitErrorHandler<Job> = (errors) => {
		watch("importGrowerName");
		toast.error("Please fill in all required fields correctly.");
		for (const errorKey of Object.keys(errors)) {
			let message = (errors as Record<string, any>)[errorKey]?.message;
			switch (errorKey) {
				case "dealIds":
				case "frequencyOfMicros":
				case "density":
				case "depth":
				case "testPackage":
					message = "Sampling Configuration Edits -> " + message;
					break;
				case "growerName":
					message = "Grower Details -> " + message;
					break;
			}
			toast.error(message);
		}
	};

	function fileFormField(
		title: string,
		helpText: string,
		key: keyof Job["files"],
		fieldType: FileType | undefined,
		data: Job,
	) {
		// Build file validation context with all necessary business logic inputs
		const fileContext: ValidationContext = {
			...validationContext,
			isPointsAttached: watch("isPointsAttached"),
		};

		// Use centralized validation logic
		const required = fieldType ? isFieldRequired(fieldType, fileContext) : false;
		const hidden = fieldType ? !isFieldVisible(fieldType, fileContext) : false;

		if (hidden) return null;

		const handleRemoveFile = async (index: number) => {
			const updatedFiles = Array.from(data.files[key] as File[]);
			updatedFiles.splice(index, 1);
			setValue(`files.${key}`, updatedFiles.length ? updatedFiles : undefined);

			// Also clear GeoJSON when file is removed
			if (fieldType?.endsWith("GeoJSON")) {
				if (updatedFiles.length === 0) {
					setValue(`files.${fieldType}`, undefined);
				}
			}

			// Trigger validation after removing file
			await trigger(`files.${key}`);
		};

		// Extract onChange from register to wrap it with validation trigger
		const registerProps = register(`files.${key}`, {
			required: required ? "File upload required for Job/Configuration type" : undefined,
		});
		const { onChange: rhfOnChange, ...restRegisterProps } = registerProps;

		return (
			<>
				<FormField
					title={title}
					required={required}
					helpText={helpText}
					// @ts-ignore
					error={errors[key]?.message}
					input={
						<Box
							sx={{
								"& > :not(style)": { m: 1 },
							}}
						>
							<input
								accept="*/*"
								style={{ display: "none" }}
								id={`contained-button-file-${key}`}
								multiple
								type="file"
								{...restRegisterProps}
								onChange={async (e) => {
									await rhfOnChange(e); // Call react-hook-form's onChange first
									await trigger(`files.${key}`); // Then trigger validation

									// Convert to GeoJSON if this is a geo file field
									const files = e.target.files;
									if (files && files.length > 0 && fieldType?.endsWith("GeoJSON")) {
										try {
											const geoJSON = await GeoData.toGeoJSON(files[0], "SHP");
											setValue(`files.${fieldType}`, geoJSON);
										} catch (error) {
											console.error(`Failed to convert ${key} to GeoJSON:`, error);
										}
									}
								}}
							/>
							<label htmlFor={`contained-button-file-${key}`}>
								<Button variant="contained" component="span">
									<Upload className="icon-in-button" /> Upload
								</Button>
							</label>
							{data.files && data.files[key] && (
								<div>
									{Array.from(data.files[key] ? (data.files[key] as File[]) : []).map((file, index) => (
										<Box key={index} display="flex" alignItems="center">
											<Typography>{file.name}</Typography>
											<Button
												onClick={() => handleRemoveFile(index)}
												variant="outlined"
												color="error"
												size="small"
												sx={{ ml: 1 }}
											>
												Remove
											</Button>
										</Box>
									))}
								</div>
							)}
						</Box>
					}
				/>
			</>
		);
	}

	function textFormField(
		title: string,
		helpText: string,
		validationKey: ImportTableColumn,
		options?: {
			registerName?: string;
			multiline?: boolean;
			rows?: number;
			type?: string;
			size?: "small" | "medium";
			value?: any;
			onChangeCallback?: () => void;
		},
	) {
		const registerName = options?.registerName || (validationKey as string);
		const error = errors[registerName as keyof Job]?.message;

		// Get the register props and merge with custom onChange
		const registerProps = register(registerName as any, getFormValidationRules(validationKey, validationContext));
		const { onChange: rhfOnChange, ...restRegisterProps } = registerProps;

		return (
			<FormField
				title={title}
				error={error}
				required={isFieldRequired(validationKey, validationContext)}
				hidden={!isFieldVisible(validationKey, validationContext)}
				helpText={helpText}
				input={
					<TextField
						variant="outlined"
						color={error ? "error" : "primary"}
						fullWidth
						{...(options?.size && { size: options.size })}
						{...(options?.value !== undefined && { value: options.value })}
						{...(options?.multiline && { multiline: true, rows: options.rows })}
						{...(options?.type && { type: options.type })}
						{...restRegisterProps}
						onChange={(e) => {
							rhfOnChange(e); // Call react-hook-form's onChange first
							if (options?.onChangeCallback) {
								options.onChangeCallback(); // Then call custom callback
							}
						}}
					/>
				}
			/>
		);
	}

	// Helper function to render checkbox FormField components with validation
	function checkboxFormField(
		title: string,
		helpText: string,
		validationKey: ImportTableColumn,
		options?: {
			registerName?: string;
		},
	) {
		const registerName = options?.registerName || (validationKey as string);

		return (
			<Controller
				name={registerName as any}
				control={control}
				render={({ field }) => (
					<FormField
						title={title}
						helpText={helpText}
						required={isFieldRequired(validationKey, validationContext)}
						hidden={!isFieldVisible(validationKey, validationContext)}
						input={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
					/>
				)}
			/>
		);
	}

	// Helper function to render date FormField components with validation
	function dateFormField(
		title: string,
		helpText: string,
		validationKey: ImportTableColumn,
		options?: {
			registerName?: string;
		},
	) {
		const registerName = options?.registerName || (validationKey as string);
		const error = errors[registerName as keyof Job]?.message;

		return (
			<Controller
				name={registerName as any}
				control={control}
				rules={getFormValidationRules(validationKey, validationContext)}
				render={({ field }) => (
					<FormField
						title={title}
						helpText={helpText}
						required={isFieldRequired(validationKey, validationContext)}
						hidden={!isFieldVisible(validationKey, validationContext)}
						error={error}
						input={
							<TextField
								variant="outlined"
								type="date"
								fullWidth
								InputLabelProps={{ shrink: true }}
								error={!!error}
								value={field.value ? new Date(field.value).toISOString().split("T")[0] : ""}
								onChange={(e) => {
									const dateValue = e.target.value ? new Date(e.target.value) : undefined;
									field.onChange(dateValue);
								}}
							/>
						}
					/>
				)}
			/>
		);
	}

	// Helper function to render autocomplete FormField components with validation
	function autocompleteFormField<T = string>(
		title: string,
		helpText: string,
		validationKey: ImportTableColumn,
		autocompleteOptions: T[],
		options?: {
			registerName?: string;
			getOptionLabel?: (option: T) => string;
			customValue?: any;
			customOnChange?: (newValue: T | null, field: any) => void;
		},
	) {
		const registerName = options?.registerName || (validationKey as string);
		const error = errors[registerName as keyof Job]?.message;

		return (
			<Controller
				name={registerName as any}
				control={control}
				rules={getFormValidationRules(validationKey, validationContext)}
				render={({ field }) => (
					<FormField
						title={title}
						helpText={helpText}
						required={isFieldRequired(validationKey, validationContext)}
						hidden={!isFieldVisible(validationKey, validationContext)}
						error={error}
						input={
							<Autocomplete
								fullWidth
								options={autocompleteOptions}
								value={options?.customValue !== undefined ? options.customValue : field.value || null}
								onChange={(_, newValue) => {
									if (options?.customOnChange) {
										options.customOnChange(newValue, field);
									} else {
										field.onChange(newValue || undefined);
									}
								}}
								{...(options?.getOptionLabel && { getOptionLabel: options.getOptionLabel })}
								renderInput={(params) => <TextField {...params} variant="outlined" error={!!error} />}
							/>
						}
					/>
				)}
			/>
		);
	}

	return (
		<>
			{loading && (
				<Box
					sx={{
						position: "fixed",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						backgroundColor: "rgba(255, 255, 255, 0.8)",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						zIndex: 1300,
					}}
				>
					<LoadingComponent />
					<Typography variant="h5">Submitting Job...</Typography>
				</Box>
			)}
			<Grid
				alignItems="stretch"
				alignContent="stretch"
				container
				maxWidth="md"
				margin="auto"
				component="form"
				spacing={1}
				onSubmit={handleSubmit(onSubmit, onError)}
			>
				<Grid item xs={12} className="single-upload-group">
					<Accordion defaultExpanded={false}>
						{/* 
						// ==========================================================================================
						// IMPORT_FIELDS: If you are adding a new column, please also add it to all other places with this comment.
						// ==========================================================================================
						 */}
						<AccordianSummary expandIcon={<ExpandMoreIcon />}>
							<Tooltip title="Change your Sampling configuration here" arrow>
								<Typography variant="h6">Sampling Configuration Edits</Typography>
							</Tooltip>
						</AccordianSummary>
						<AccordionDetails>
							<Grid alignItems="stretch" alignContent="stretch" container maxWidth="md" margin="auto" spacing={1}>
								<FormField
									title="Update Deal:"
									helpText="Select a deal"
									required={isFieldRequired("deal", validationContext)}
									hidden={!isFieldVisible("deal", validationContext)}
									error={errors.dealIds?.message}
									input={
										<Autocomplete
											fullWidth={true}
											size="small"
											options={[...props.allDeals, { id: "custom", label: "Create New Configuration" }]}
											isOptionEqualToValue={(option, value) => option.id === value.id}
											getOptionKey={(option) => option.id}
											value={
												props.allDeals.find((deal) => deal.id === watch("dealIds")?.at(0)) || {
													id: "custom",
													label: "Create New Configuration",
												}
											}
											renderInput={(params) => <TextField {...params} />}
											// onBlur allows searching for the deal by name
											onBlur={(event) => {
												const inputValue = (event.target as HTMLInputElement).value;
												const selectedDeal = (props.allDeals.find(
													(deal) => getDealLabelForDropdown(deal) === inputValue,
												) || {
													id: "custom",
													label: "Create New Configuration",
												}) as Deal;

												if (selectedDeal.id === "custom") {
													setDeal({ id: "custom", label: "Create New Configuration" });
													setValue("dealIds", ["custom"]);
												} else {
													setDeal(selectedDeal);
													setValue("dealIds", [selectedDeal.id]);
													setValue("frequencyOfMicros", props.selectedDeal.frequencyOfMicros);
													setValue("density", props.selectedDeal.density);
													setValue("depth", props.selectedDeal.depth);
													const labPackage = props.selectedDeal.testPackage;
													setValue(
														"testPackage",
														Array.isArray(labPackage) ? labPackage[0] : labPackage || "No test package specified",
													);
												}
											}}
											getOptionLabel={getDealLabelForDropdown}
										/>
									}
								/>
								{textFormField(
									columnDisplayNames.frequencyOfMicros,
									"Frequency of Micros in the configuration",
									"frequencyOfMicros",
									{
										size: "medium",
										value: watch("frequencyOfMicros"),
										onChangeCallback: updateDeal,
									},
								)}
								{textFormField(columnDisplayNames.depth, "Enter the depth in inches", "depth", {
									onChangeCallback: updateDeal,
								})}
								{textFormField(columnDisplayNames.density, "Enter the density of the configuration", "density", {
									onChangeCallback: updateDeal,
								})}
								{textFormField(
									columnDisplayNames.testPackage,
									"Enter the lab package for the configuration",
									"testPackage",
									{
										onChangeCallback: updateDeal,
									},
								)}
							</Grid>
						</AccordionDetails>
					</Accordion>
				</Grid>

				<Grid item xs={12} className="single-upload-group">
					<Accordion defaultExpanded={true}>
						<AccordianSummary expandIcon={<ExpandMoreIcon />}>
							<PortalTooltip title="Grower Details">
								<Typography variant="h6">Grower Details</Typography>
							</PortalTooltip>
						</AccordianSummary>
						<AccordionDetails>
							<Grid alignItems="stretch" alignContent="stretch" container maxWidth="md" margin="auto" spacing={1}>
								<FormField
									title={"Grower Name"}
									required={isFieldRequired("grower", validationContext)}
									hidden={!isFieldVisible("grower", validationContext)}
									error={errors.growerName?.message}
									input={
										<Controller
											name="importGrowerName"
											control={control}
											rules={{
												required: isFieldRequired("grower", validationContext) ? "Grower Name is Required" : false,
											}}
											render={() => (
												<Autocomplete
													fullWidth={true}
													value={growerSelected}
													options={[...props.growers]}
													freeSolo
													renderInput={(params) => <TextField {...params} label="Grower Name" variant="outlined" />}
													getOptionKey={(option) => {
														if (typeof option === "string") {
															return option;
														}
														return (option as Grower).id || (option as Grower).name;
													}}
													filterOptions={(options, params) => {
														const filtered = filter(options, params);

														if (
															params.inputValue !== "" &&
															!props.growers.some(
																(grower) => grower.name.toLowerCase() === params.inputValue.toLowerCase(),
															)
														) {
															filtered.push({
																id: CREATE_NEW_GROWER,
																name: `New Grower: ${params.inputValue}`,
															});
														}

														return filtered;
													}}
													getOptionLabel={(grower) => {
														if (
															watch("growerIds")?.at(0) !== CREATE_NEW_GROWER &&
															watch("growerIds")?.at(0) !== undefined &&
															watch("growerIds")?.at(0) !== ""
														) {
															return (grower as Grower)?.name || (grower as string);
														}
														if (typeof grower === "string") {
															return grower.startsWith("New Grower") ? grower : `New Grower: ${grower}`;
														}

														return (grower as Grower)?.name ?? "";
													}}
													onChange={(event, value) => {
														if (event.type == "blur" || event.type == "keydown") {
															if (typeof value === "string") {
																const newValue = (value as string).startsWith("New Grower:")
																	? value.replace("New Grower: ", "")
																	: value;
																if (
																	newValue !== "" &&
																	!props.growers.some((grower) => grower.name.toLowerCase() === newValue.toLowerCase())
																) {
																	setValue("importGrowerName", newValue);
																	setValue("growerIds", [CREATE_NEW_GROWER]);
																	setValue("growerPhoneNumber", "");
																	setGrowerSelected({ id: CREATE_NEW_GROWER, name: value });
																	trigger("importGrowerName");
																	return;
																}
																const grower = props.growers.find(
																	(grower) => grower.name.toLowerCase() === newValue.toLowerCase(),
																);
																if (grower) {
																	setValue("importGrowerName", grower.name as string);
																	setValue("growerIds", [grower?.id]);
																	setValue("growerPhoneNumber", grower.phoneNumber || "");
																	setGrowerSelected(grower as Grower);
																}
															} else if (value && value.id === CREATE_NEW_GROWER) {
																setValue("importGrowerName", (value as Grower)?.name ?? "");
																setValue("growerIds", [CREATE_NEW_GROWER]);
																setValue("growerPhoneNumber", "");
																setGrowerSelected(value);
																trigger("importGrowerName");
															} else if (typeof value === "object") {
																setValue("importGrowerName", (value as Grower)?.name ?? "");
																setValue("growerIds", value?.id ? [value?.id] : []);
																setValue("growerPhoneNumber", (value as Grower)?.phoneNumber || "");
																setGrowerSelected(value as Grower);
															}
															return;
														}
														if (typeof value === "string") {
															setValue("importGrowerName", value);
															setValue("growerIds", [CREATE_NEW_GROWER]);
															setValue("growerPhoneNumber", "");
															setGrowerSelected({ id: CREATE_NEW_GROWER, name: value });
														} else if (value && value.id === CREATE_NEW_GROWER) {
															setValue("importGrowerName", (value as Grower)?.name ?? "");
															setValue("growerIds", [CREATE_NEW_GROWER]);
															setValue("growerPhoneNumber", "");
															setGrowerSelected(value);
														} else {
															setValue("importGrowerName", (value as Grower)?.name ?? "");
															setValue("growerIds", value?.id ? [value?.id] : []);
															setValue("growerPhoneNumber", (value as Grower)?.phoneNumber || "");
															setGrowerSelected(value as Grower);
														}
														trigger("importGrowerName");
													}}
													autoSelect={true}
													selectOnFocus
													clearOnBlur
													handleHomeEndKeys
												/>
											)}
										/>
									}
								/>

								{textFormField("Grower Cell Number", "Enter grower's cell phone number", "growerPhoneNumber")}
							</Grid>
						</AccordionDetails>
					</Accordion>
				</Grid>

				<Grid item xs={12} className="single-upload-group">
					<Accordion defaultExpanded={true}>
						<AccordianSummary expandIcon={<ExpandMoreIcon />}>
							<PortalTooltip title="Job Data">
								<Typography variant="h6">Job Data</Typography>
							</PortalTooltip>
						</AccordianSummary>
						<AccordionDetails>
							<Grid alignItems="stretch" alignContent="stretch" container maxWidth="md" margin="auto" spacing={1}>
								{props.branches &&
									props.branches.length > 0 &&
									autocompleteFormField<Branch>(
										columnDisplayNames.branch,
										"Select the branch for this job",
										"branch",
										props.branches || [],
										{
											registerName: "branchIds",
											getOptionLabel: (branch) => branch.name,
											customValue: selectedBranch,
											customOnChange: (newValue, field) => {
												setSelectedBranch(newValue);
												field.onChange(newValue ? [newValue.id] : []);
											},
										},
									)}
								{textFormField(columnDisplayNames.field, "Enter the name of the field", "field", {
									registerName: "fieldName",
								})}
								{textFormField(columnDisplayNames.farm, "Only required if field name is not unique", "farm", {
									registerName: "farmName",
								})}
								{textFormField(columnDisplayNames.fieldId, "Unique identifier for this field", "fieldId")}
								{autocompleteFormField(
									columnDisplayNames.lastCrop,
									"Previous crop grown in this field",
									"lastCrop",
									props.lastCropOptions ?? [],
								)}
								{autocompleteFormField(
									columnDisplayNames.nextCrop,
									"Next crop to be planted in this field",
									"nextCrop",
									props.nextCropOptions ?? [],
								)}
								{textFormField(
									columnDisplayNames.yieldGoal,
									"Target yield for this field (e.g., '200 bu/ac')",
									"yieldGoal",
								)}
								{dateFormField(
									columnDisplayNames.dateFieldReadyEstimated,
									"When do you estimate this field will be ready for sampling?",
									"dateFieldReadyEstimated",
								)}

								<FormField
									title={columnDisplayNames.salesperson}
									helpText="Select or add a salesperson"
									required={isFieldRequired("salesperson", validationContext)}
									hidden={!isFieldVisible("salesperson", validationContext)}
									input={
										<Controller
											name="salespersonIds"
											control={control}
											rules={{
												required: isFieldRequired("salesperson", validationContext) ? "Salesperson is required" : false,
											}}
											render={() => (
												<Autocomplete
													fullWidth
													value={salespersonSelected}
													options={props.salespersons ?? []}
													freeSolo
													renderInput={(params) => <TextField {...params} label="Salesperson" variant="outlined" />}
													getOptionKey={(option) => {
														if (typeof option === "string") return option;
														return option.id || option.name;
													}}
													filterOptions={(options, params) => {
														const filtered = filter(options, params);

														if (
															params.inputValue !== "" &&
															!(props.salespersons ?? []).some(
																(sp) => sp.name.toLowerCase() === params.inputValue.toLowerCase(),
															)
														) {
															filtered.push({
																id: CREATE_NEW_SALESPERSON,
																name: `New Salesperson: ${params.inputValue}`,
															});
														}

														return filtered;
													}}
													getOptionLabel={(salesperson) => {
														if (
															watch("salespersonIds")?.at(0) !== CREATE_NEW_SALESPERSON &&
															watch("salespersonIds")?.at(0) !== undefined &&
															watch("salespersonIds")?.at(0) !== ""
														) {
															return (salesperson as Contact)?.name || (salesperson as string);
														}
														if (typeof salesperson === "string") {
															return salesperson.startsWith("New Salesperson")
																? salesperson
																: `New Salesperson: ${salesperson}`;
														}

														return (salesperson as Contact)?.name ?? "";
													}}
													onChange={(event, value) => {
														if (typeof value === "string") {
															const newValue = value.startsWith("New Salesperson:")
																? value.replace("New Salesperson: ", "")
																: value;

															if (
																newValue !== "" &&
																!(props.salespersons ?? []).some(
																	(sp) => sp.name.toLowerCase() === newValue.toLowerCase(),
																)
															) {
																setValue("salespersonIds", [CREATE_NEW_SALESPERSON]);
																setValue("newSalesperson", { name: newValue, phone: "" });
																setSalespersonSelected({ id: CREATE_NEW_SALESPERSON, name: value });
																trigger("salespersonIds");
																trigger("newSalesperson.phone");
																return;
															}

															const salesperson = (props.salespersons ?? []).find(
																(sp) => sp.name.toLowerCase() === newValue.toLowerCase(),
															);
															if (salesperson) {
																setValue("salespersonIds", [salesperson.id]);
																setValue("newSalesperson", undefined);
																setSalespersonSelected(salesperson);
																trigger("newSalesperson.phone");
															}
														} else if (value && value.id === CREATE_NEW_SALESPERSON) {
															const name = (value as Contact).name.replace("New Salesperson: ", "");
															setValue("salespersonIds", [CREATE_NEW_SALESPERSON]);
															setValue("newSalesperson", { name, phone: "" });
															setSalespersonSelected(value);
															trigger("salespersonIds");
															trigger("newSalesperson.phone");
														} else if (value) {
															setValue("salespersonIds", [value.id]);
															setValue("newSalesperson", undefined);
															setSalespersonSelected(value as Contact);
															trigger("newSalesperson.phone");
														} else {
															setValue("salespersonIds", []);
															setValue("newSalesperson", undefined);
															setSalespersonSelected(null);
															trigger("newSalesperson.phone");
														}
													}}
													autoSelect
													selectOnFocus
													clearOnBlur
													handleHomeEndKeys
												/>
											)}
										/>
									}
								/>

								<FormField
									title={columnDisplayNames.salespersonMobilePhone}
									helpText="Salesperson mobile phone number"
									required={isFieldRequired("salespersonMobilePhone", validationContext)}
									hidden={!isFieldVisible("salespersonMobilePhone", validationContext)}
									error={
										watch("salespersonIds")?.at(0) === CREATE_NEW_SALESPERSON
											? errors.newSalesperson?.phone?.message
											: undefined
									}
									input={
										<Controller
											name="newSalesperson.phone"
											control={control}
											rules={
												watch("salespersonIds")?.at(0) === CREATE_NEW_SALESPERSON
													? getFormValidationRules("salespersonMobilePhone", validationContext)
													: {}
											}
											render={({ field }) => (
												<TextField
													{...field}
													variant="outlined"
													fullWidth
													disabled={watch("salespersonIds")?.at(0) !== CREATE_NEW_SALESPERSON}
													value={
														watch("salespersonIds")?.at(0) === CREATE_NEW_SALESPERSON
															? field.value || ""
															: (props.salespersons ?? []).find((sp) => sp.id === watch("salespersonIds")?.at(0))
																	?.mobilePhone || ""
													}
													onChange={(e) => {
														if (watch("salespersonIds")?.at(0) === CREATE_NEW_SALESPERSON) {
															field.onChange(e.target.value);
														}
													}}
													placeholder={
														watch("salespersonIds")?.at(0) === CREATE_NEW_SALESPERSON ? "Enter phone number" : ""
													}
													error={
														watch("salespersonIds")?.at(0) === CREATE_NEW_SALESPERSON && !!errors.newSalesperson?.phone
													}
												/>
											)}
										/>
									}
								/>

								{textFormField(
									columnDisplayNames.eventId,
									"This is used to sync your data with the appropriate software",
									"eventId",
								)}
								{checkboxFormField(
									columnDisplayNames.isReadyToSample,
									"Select if field is ready to sample now",
									"isReadyToSample",
								)}
								{isFieldVisible("fieldPriority", validationContext) && (
									<FormField
										title={columnDisplayNames.fieldPriority}
										helpText="Select the priority of the field"
										required={isFieldRequired("fieldPriority", validationContext)}
										input={
											<Select
												fullWidth
												value={watch("fieldPriority")}
												{...register("fieldPriority", getFormValidationRules("fieldPriority", validationContext))}
											>
												{getJobPriorities().map((priority, index) => {
													return (
														<MenuItem key={index} value={priority}>
															{priority}
														</MenuItem>
													);
												})}
											</Select>
										}
									/>
								)}

								{fileFormField(
									columnDisplayNames.boundaryGeoJSON,
									"This is for your field boundary shp file",
									"boundarySHP",
									"boundaryGeoJSON",
									watch(),
								)}

								{fileFormField(
									columnDisplayNames.zonesGeoJSON,
									"This is for your Zone shp file",
									"zonesSHP",
									"zonesGeoJSON",
									watch(),
								)}

								<Controller
									name="isPointsAttached"
									control={control}
									render={({ field }) => (
										<FormField
											title="Attach Points?"
											helpText="Select if you want to attach points (points should be attached for Zones and ModGrid), then upload points file"
											input={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
										/>
									)}
								/>

								{fileFormField(
									columnDisplayNames.pointsGeoJSON,
									"This is for your point shp file",
									"pointsSHP",
									"pointsGeoJSON",
									watch(),
								)}

								<Controller
									name="microsList"
									control={control}
									defaultValue={""}
									rules={getFormValidationRules("microsList", validationContext)}
									render={({ field }) => (
										<FormField
											title={columnDisplayNames.microsList}
											helpText="Specify sample ids separated by comma"
											required={isFieldRequired("microsList", validationContext)}
											input={
												<TextField
													variant="outlined"
													fullWidth
													color={errors.microsList ? "error" : "primary"}
													value={microsRaw}
													onChange={(e) => setMicrosRaw(e.target.value)}
													onBlur={() => {
														const parsed = commaStringToUniqueAlphanumericList(microsRaw);
														field.onChange(parsed);
														setMicrosRaw(parsed);
													}}
												/>
											}
											error={errors.microsList?.message}
											hidden={!isFieldVisible("microsList", validationContext)}
										/>
									)}
								/>
								{checkboxFormField(
									columnDisplayNames.collectBoundary,
									"Select this is you want us to collect a boundary on your field",
									"collectBoundary",
								)}
								{textFormField(
									columnDisplayNames.labSubmissionCode,
									"This is the code for the lab submission",
									"labSubmissionCode",
								)}
								{textFormField(
									columnDisplayNames.labInstructions,
									"Enter any special notes for lab",
									"labInstructions",
									{
										multiline: true,
										rows: 3,
									},
								)}
								{textFormField(
									columnDisplayNames.submissionNotes,
									"Enter any notes for the submission(restrictions, special notes)",
									"submissionNotes",
									{ multiline: true, rows: 3 },
								)}

								{fileFormField(
									columnDisplayNames.fieldNotes,
									"This is for any additional field files, like pullin instructions",
									"fieldNotes",
									"fieldNotes",
									watch(),
								)}
								{checkboxFormField(columnDisplayNames.isTestJob, "Enable if this is a testing job", "isTestJob")}
								{textFormField(columnDisplayNames.labName, "Enter the name of the lab", "labName")}
								{textFormField(columnDisplayNames.externalLabId, "Enter the external lab ID", "externalLabId")}
								{textFormField(columnDisplayNames.externalId, "Enter the external ID", "externalId")}
								{textFormField(columnDisplayNames.externalFarmId, "Enter the external farm ID", "externalFarmId")}
								{textFormField(
									columnDisplayNames.submitterName,
									"Name of the person submitting this job",
									"submitterName",
								)}
								{textFormField(
									columnDisplayNames.submitterEmail,
									"Email address of the person submitting this job",
									"submitterEmail",
									{
										type: "email",
									},
								)}
							</Grid>
						</AccordionDetails>
					</Accordion>
				</Grid>
				<div style={{ padding: 5, paddingLeft: 8 }}>
					<Typography>
						{" "}
						Fields marked with <a style={{ color: "red" }}>*</a> are required
					</Typography>
				</div>

				<Grid item xs={12} className="single-upload-group">
					<Button fullWidth type="submit" variant="contained" color="primary" disabled={!isValid}>
						<Publish className="icon-in-button" />
						Create Job
					</Button>
				</Grid>
			</Grid>
			<br />
			<br />
			<br />
			<br />
			<br />
		</>
	);
}

export function commaStringToUniqueAlphanumericList(value: string | undefined): string {
	if (!value) {
		return "";
	}

	const newList = Array.from(
		new Set(
			value
				.split(",")
				.map((s) => s.trim())
				.filter((s) => /^[a-zA-Z0-9]+$/.test(s)),
		),
	);

	return newList.join(",");
}
